// Deterministic prose for the Written timeline view. Takes the same
// structured events that drive the Visual infographic and formats
// them as paragraphs — no AI call, no randomness, no two-pass
// retries. Everything reads from fields the AI already produced
// during the single timeline generation.
//
// Shape of the output: plain text with blank-line paragraph
// separators. Each event carries a `[rec:ID]` provenance token at the
// end of its sentence; the app strips it in the normal view (footnotes
// in "View sources") and the export layer rewrites it to a "Record #N"
// reference.

import type { Case, TimelineEvent } from "../types";
import { formatDate, formatMonthYear } from "./formatDate";

interface RenderArgs {
  events: TimelineEvent[];
  caseData: Case;
  // Pre-built by the caller via exportShared.buildStatusParagraph —
  // it needs `records` (and optionally `volumeBriefs`) that the
  // prose renderer shouldn't have to re-thread. Passing it in keeps
  // the renderer pure.
  statusParagraph: string;
}

// All date formatting in this module routes through lib/formatDate
// so the prose never drifts a day west of its storage value.
function formatDateLong(ymd: string): string {
  return formatDate(ymd, "long") || ymd;
}

function monthKey(ymd: string): string {
  return ymd.slice(0, 7);
}

function monthHeading(ymdPrefix: string): string {
  return formatMonthYear(ymdPrefix) || ymdPrefix;
}

// Ensure the sentence ends with a single terminating punctuation.
// The AI is told to write complete sentences, but some drop the
// period, and some add trailing whitespace — normalise so paragraph
// joins don't produce double spaces or bare fragments.
function normaliseSentence(s: string): string {
  const trimmed = s.replace(/\s+/g, " ").trim();
  if (!trimmed) return "";
  if (/[.!?]["')\]]*$/.test(trimmed)) return trimmed;
  return `${trimmed}.`;
}

// "Alice, Bob and Carol" — Oxford-free, English-only. Fine for prose
// framing; we're not localising the narrative at this layer.
function humanList(items: string[]): string {
  const cleaned = items.map((s) => s.trim()).filter(Boolean);
  if (cleaned.length === 0) return "";
  if (cleaned.length === 1) return cleaned[0]!;
  if (cleaned.length === 2) return `${cleaned[0]} and ${cleaned[1]}`;
  return `${cleaned.slice(0, -1).join(", ")} and ${cleaned[cleaned.length - 1]}`;
}

function lowercaseFirst(s: string): string {
  if (!s) return s;
  // Don't lowercase acronyms / proper nouns the user typed with
  // deliberate caps — only drop the case when the first character is
  // followed by a lower-case letter (i.e. looks like a sentence-start
  // capital, not an acronym).
  if (s.length >= 2 && /[A-Z][a-z]/.test(s.slice(0, 2))) {
    return s[0]!.toLowerCase() + s.slice(1);
  }
  return s;
}

// Aggregate the most-mentioned parties across every event. Used to
// build the opening "This case involves X, Y and Z…" sentence.
// Dedupes case-insensitively while preserving the first-seen casing.
function topParties(events: TimelineEvent[], cap: number): string[] {
  const tally = new Map<string, { display: string; count: number }>();
  for (const ev of events) {
    const parties = ev.parties ?? (ev.party ? [ev.party] : []);
    const seen = new Set<string>(); // avoid double-counting within one event
    for (const raw of parties) {
      const name = (raw ?? "").trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const prior = tally.get(key);
      if (prior) prior.count += 1;
      else tally.set(key, { display: name, count: 1 });
    }
  }
  const ranked = Array.from(tally.values()).sort(
    (a, b) => b.count - a.count || a.display.localeCompare(b.display),
  );
  return ranked.slice(0, cap).map((p) => p.display);
}

// Opening sentence framing the case for a reader who hasn't seen it
// before. Uses the structured parties + the first sentence of the
// case summary when available — falls back to the case name when
// there's no summary to anchor on.
function renderOpening(events: TimelineEvent[], caseData: Case): string {
  const parties = topParties(events, 3);
  const summary = (caseData.summary ?? "").trim();
  const firstSentence = summary.split(/(?<=[.!?])\s+/)[0]?.trim() ?? "";
  const subject = firstSentence
    ? lowercaseFirst(firstSentence.replace(/[.!?]$/, ""))
    : caseData.name.trim();
  if (parties.length > 0) {
    return normaliseSentence(
      `This case involves ${humanList(parties)} regarding ${subject}`,
    );
  }
  return normaliseSentence(`This case concerns ${subject}`);
}

// Turn one event into one sentence. The summary is the workhorse;
// amounts are surfaced inline so the reader sees the dollar value
// without having to click through. Each sentence ends with the
// citation token in its canonical [Record YYYY-MM-DD] form so the
// consumer can rewrite it to an inline link or a numbered reference.
function renderEventSentence(ev: TimelineEvent): string {
  const date = formatDateLong(ev.date);
  const body = (ev.summary ?? ev.detail ?? "").trim();
  // Fallback for mechanical-only events where enrichment failed:
  // synthesise from title + party so the paragraph still reads.
  const fallback = ev.title
    ? ev.party
      ? `${ev.title} (${ev.party})`
      : ev.title
    : ev.party || "an update was logged";
  const sentence = body || fallback;
  // Citations are already in canonical "[rec:ID]" form (set deterministically
  // on every event), so append as-is — do NOT re-wrap in brackets.
  const citation = (ev.citations && ev.citations[0]) ?? "";
  // Embed amounts that weren't mentioned verbatim in the summary —
  // the AI is instructed to inline amounts in its summary but older
  // cached events may not have, so we append them for completeness.
  const amounts = ev.amounts ?? (ev.amount ? [ev.amount] : []);
  const missingAmounts = amounts.filter(
    (a) => a && !sentence.toLowerCase().includes(a.toLowerCase()),
  );
  const amountClause =
    missingAmounts.length > 0 ? ` (${missingAmounts.join(", ")})` : "";
  return normaliseSentence(
    `On ${date}, ${lowercaseFirst(sentence)}${amountClause}${
      citation ? ` ${citation}` : ""
    }`,
  );
}

// Public entry point. Returns a single string; paragraphs separated
// by blank lines. Callers render each paragraph, stripping [rec:ID]
// tokens for display.
export function renderProseFromEvents({
  events,
  caseData,
  statusParagraph,
}: RenderArgs): string {
  if (events.length === 0) {
    // Nothing to narrate — still emit an opening so exports and the
    // Written view have something to render beyond the status.
    const opening = renderOpening([], caseData);
    const paragraphs = [opening, statusParagraph.trim()].filter(Boolean);
    return paragraphs.join("\n\n");
  }

  const sorted = [...events].sort((a, b) => a.date.localeCompare(b.date));

  const paragraphs: string[] = [renderOpening(sorted, caseData)];

  // Group by calendar month so each paragraph is visually scoped to
  // one period. Within a month, events print in chronological order
  // — same order the Visual view uses.
  let currentMonth = "";
  let buffer: string[] = [];
  const flushBuffer = (heading: string) => {
    if (buffer.length === 0) return;
    const body = buffer.join(" ");
    paragraphs.push(heading ? `${heading}. ${body}` : body);
    buffer = [];
  };
  for (const ev of sorted) {
    const m = monthKey(ev.date);
    if (m !== currentMonth) {
      // New month: flush the previous month's buffer before starting.
      flushBuffer(
        currentMonth ? `In ${monthHeading(currentMonth + "-01")}` : "",
      );
      currentMonth = m;
    }
    buffer.push(renderEventSentence(ev));
  }
  // Final month flush.
  flushBuffer(
    currentMonth ? `In ${monthHeading(currentMonth + "-01")}` : "",
  );

  const status = statusParagraph.trim();
  if (status) paragraphs.push(status);
  return paragraphs.join("\n\n");
}
